/* oxlint-disable react/jsx-pascal-case */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import classnames from 'classnames';

import { Text, TextType } from 'ui-components/components/text';
import { Button } from 'ui-components/components/button';
import { Popover } from 'ui-components/components/popover';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import DataDrivenLogo from '../../../../components/data-driven-logo';
import GetProviderDisplayName from '../../../../components/data-driven-logo/get-provider-display-name';
import unishippersIcon from '../../../../svgs/unishippers-icon.svg';
import wwexIcon from '../../../../svgs/wwex-icon.svg';
import { useSellerLabelProvider } from '../../../../hooks/use-seller-label-provider';
import { useSellerLabelProviders } from '../../../../hooks/use-seller-label-providers';
import ServiceText from '../../providers/providers-list/service-text';
import EditSettingsButton from '../../providers/providers-list/shipping-providers-table/edit-setting-button';
import { useServiceVisibilityContext } from '../../providers/service-visibility-modal/context-provider';
import { TD } from '../../providers/providers-list/reuseable-table/components';
import { BalanceInfo } from './balance-info';
import { CarrierEnums, CarrierStatus } from '../../../../enums/carriers';
import { Providers } from '../../../../enums/providers';
import { OnboardingStatusPopoverContent } from './components/onboarding-status-popover-content';
import { BusinessDateMomentService } from '../../../../services/moment-business-day';
import { CopyToClipboardButton } from 'ui-components/components/copy-to-clipboard-button/copy-to-clipboard-button';
import { useIsCarrierRowDisabled } from '../../../../hooks/non-wallet-access-hooks';

import messages from '../messages';
import style from './carriers-additional.module.less';
import { Tag, TagType } from 'ui-components/components/tag';
import { css } from 'emotion';
import moment from 'moment';

const popoverTipShapeStyles = css`
  fill: rgb(238, 238, 238);
`;

const MAIL_TO = 'royalmailsupport@shipstation.com';

const LOCAL_ICON_OVERRIDES = {
  [Providers.WWEX.labelProviderIdInt]: wwexIcon,
  [Providers.Unishippers.labelProviderIdInt]: unishippersIcon,
};

export const CarrierRow = ({ sellerLabelProviderId, showCarrierId }) => {
  const intl = useIntl();

  const sellerLabelProvider = useSellerLabelProvider(sellerLabelProviderId) || {};
  const { labelProviderId, name: nickname, isPrimaryAccount, isBalanceMaintained, status } = sellerLabelProvider;
  const labelProviderIdNumber = Number.parseInt(labelProviderId, 10);
  const displayNameMessageDescriptor = GetProviderDisplayName(labelProviderIdNumber);
  const { onManageServices } = useServiceVisibilityContext();
  const sellerLabelProvidersForProvider = useSellerLabelProviders(labelProviderId);
  const { createDateUtc } = useSellerLabelProvider(sellerLabelProviderId);
  const hasMultipleAccountsForProvider = sellerLabelProvidersForProvider.length > 1;
  const showDefaultIndicator = isPrimaryAccount && hasMultipleAccountsForProvider;
  const isActiveCarrier = status === CarrierStatus.Active;
  const isPendingCarrier = status === CarrierStatus.Pending;
  const carriersWithPendingStatus = [CarrierEnums.RoyalMail.carrierId, CarrierEnums.ParcelForceRoyalMail.carrierId];

  const today = moment.utc();

  const showWarningMessage = useMemo(() => {
    if (!moment(createDateUtc).isValid()) return false;
    const momentBusinessDays = new BusinessDateMomentService(today);
    return momentBusinessDays.businessDiff(createDateUtc, true) > 5;
  }, [today, createDateUtc]);

  const showPendingApproval = isPendingCarrier && carriersWithPendingStatus.includes(labelProviderId);

  const [isOpen, setIsOpen] = useState(false);

  const popoverWrapperRef = useRef(null);
  const targetRef = useRef(null);

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const togglePopover = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const props = {
    appendTarget: document.querySelector('#app-root'),
    body: <OnboardingStatusPopoverContent mailTo={MAIL_TO} />,
    isOpen,
    onOuterAction: closePopover,
    place: 'below',
    popoverTipShapeStyles,
    ref: popoverWrapperRef,
    tipSize: 10,
  };

  const formattedDate = <FormattedDate day='2-digit' month='long' value={createDateUtc} />;

  const shouldRowShowHalfOpacity = useIsCarrierRowDisabled(labelProviderId);

  const localIconOverride = LOCAL_ICON_OVERRIDES[labelProviderIdNumber];

  return (
    <tr>
      <TD
        className={classnames(style['provider-name-cell'], {
          [style['half-opacity']]: shouldRowShowHalfOpacity,
        })}
      >
        <div className={style['icon-wrapper']}>
          <div className={style['provider-row-logo']}>
            {localIconOverride ? (
              <img alt={intl.formatMessage(displayNameMessageDescriptor, displayNameMessageDescriptor.values)} src={localIconOverride} />
            ) : (
              <DataDrivenLogo logoType='icon' providerId={labelProviderIdNumber} />
            )}
          </div>
          <div className={style['left-spacer']}>
            <div className={classnames(style['name-row'], style['icon-part'])}>
              <span>
                <FormattedMessage {...displayNameMessageDescriptor} values={displayNameMessageDescriptor.values} />
              </span>
              <span>{nickname}</span>
              {showPendingApproval && (
                <>
                  <Tag
                    className={style['status-label']}
                    message={intl.formatMessage(messages.statusPending)}
                    type={TagType.Neutral}
                    wrapperClassname={style['status-label-wrapper']}
                  />
                  {showWarningMessage && (
                    <Popover {...props}>
                      <div ref={targetRef} onClick={togglePopover}>
                        <Tooltip position='bottom-right'>
                          <div className={style['icon-part']} data-testid='icon-info'>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </div>
                          <TooltipContent contentClassName={style['tooltip-body']}>
                            <FormattedMessage {...messages.warningTagTooltipHover} />
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </Popover>
                  )}
                  <Text
                    className={classnames({
                      [style['text-right']]: !showWarningMessage,
                    })}
                    textType={TextType.Caption}
                    value={<FormattedMessage {...messages.statusPendingDate} values={{ formattedDate }} />}
                  />
                </>
              )}
            </div>
            <div className={style['details-row']}>
              {showDefaultIndicator && <FormattedMessage {...messages.default} />}
              {showDefaultIndicator && isBalanceMaintained && <>&nbsp;•&nbsp;</>}
              {isBalanceMaintained && <BalanceInfo sellerLabelProviderId={sellerLabelProviderId} />}
            </div>
          </div>
        </div>
      </TD>
      <TD
        className={classnames({
          [style['half-opacity']]: shouldRowShowHalfOpacity,
        })}
      >
        <Button
          className={style['small-link']}
          disabled={!isActiveCarrier}
          onClick={() => onManageServices(labelProviderId, sellerLabelProviderId)}
          theme='link'
        >
          <ServiceText sellerLabelProviderId={sellerLabelProviderId} />
        </Button>
      </TD>
      {showCarrierId && (
        <TD className={style['id-cell']}>
          {sellerLabelProviderId && (
            <>
              <span>{`se-${sellerLabelProviderId}`}</span>
              <CopyToClipboardButton
                textToCopy={`se-${sellerLabelProviderId}`}
                tooltipMessageDescriptor={messages.copyCarrierIdButtonLabel}
                tooltipPosition='top-right'
              />
            </>
          )}
        </TD>
      )}
      <TD
        className={classnames(style['actions-cell'], {
          [style['half-opacity']]: shouldRowShowHalfOpacity,
        })}
      >
        <EditSettingsButton sellerLabelProviderId={sellerLabelProviderId} />
      </TD>
    </tr>
  );
};

CarrierRow.propTypes = {
  sellerLabelProviderId: PropTypes.string.isRequired,
};
